"use client";

import { Activity, ArrowRight, Box, GitBranch, Hammer, Rocket, TestTube as TestTube2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pipeline = [{
    label: "Consult",
    status: "00:00s",
    icon: GitBranch,
    state: "done"
  }, {
    label: "Design",
    status: "00:12s",
    icon: Hammer,
    state: "done"
  }, {
    label: "Develop",
    status: "00:34s",
    icon: TestTube2,
    state: "done"
  }, {
    label: "Review",
    status: "00:48s",
    icon: Box,
    state: "done"
  }, {
    label: "Launch",
    status: "LIVE",
    icon: Rocket,
    state: "live"
  }];
  return <section data-section-id="9482" id="hero" className="bg-background text-foreground py-32 md:py-44 px-6 md:px-10 overflow-hidden">
      <div className="mx-auto max-w-[1320px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={leftRef} className="lg:col-span-7" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 420ms cubic-bezier(0.2, 0.9, 0.3, 1), transform 420ms cubic-bezier(0.2, 0.9, 0.3, 1)"
        }}>
            <h1 className="font-default font-bold" style={{
            fontFamily: "var(--font-default)",
            fontSize: "clamp(3.25rem, 7.5vw, 6.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: 0.92
          }}>
              <span className="block">BUILD YOUR</span>
              <span className="block">
                <span aria-hidden="true" className="inline-block text-secondary" style={{
                color: "transparent",
                WebkitTextStroke: "2px var(--secondary)",
                clipPath: leftVisible ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
                transition: "clip-path 600ms cubic-bezier(0.2, 0.9, 0.3, 1) 120ms"
              }}>
                  VISION
                </span>
                <span className="sr-only">VISION</span>
              </span>
            </h1>

            <p className="mt-8 text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: 1.55,
            maxWidth: "520px"
          }}>
              Strategic design, expert development, and seamless execution. Transform your ideas into high-impact digital experiences.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="#">
                <Button className="bg-secondary text-primary-foreground hover:bg-secondary/90 border-2 border-foreground rounded-lg px-7 py-4 h-auto font-bold text-base transition-all duration-150" style={{
                boxShadow: "4px 4px 0 0 var(--foreground)"
              }} onMouseEnter={e => {
                e.currentTarget.style.boxShadow = "6px 6px 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(-2px, -2px)";
              }} onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "4px 4px 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(0, 0)";
              }} onMouseDown={e => {
                e.currentTarget.style.boxShadow = "0 0 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(4px, 4px)";
              }} onMouseUp={e => {
                e.currentTarget.style.boxShadow = "6px 6px 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(-2px, -2px)";
              }}>
                  Start project
                  <ArrowRight className="ml-1 size-4" strokeWidth={2.5} />
                </Button>
              </Link>

              <Link to="/AboutPage">
                <Button className="bg-background text-foreground hover:bg-muted border-2 border-foreground rounded-lg px-7 py-4 h-auto font-bold text-base transition-all duration-150" style={{
                boxShadow: "4px 4px 0 0 var(--foreground)"
              }} onMouseEnter={e => {
                e.currentTarget.style.boxShadow = "6px 6px 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(-2px, -2px)";
              }} onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "4px 4px 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(0, 0)";
              }} onMouseDown={e => {
                e.currentTarget.style.boxShadow = "0 0 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(4px, 4px)";
              }} onMouseUp={e => {
                e.currentTarget.style.boxShadow = "6px 6px 0 0 var(--foreground)";
                e.currentTarget.style.transform = "translate(-2px, -2px)";
              }}>
                  Learn more
                </Button>
              </Link>
            </div>

            <p className="mt-8 font-mono text-muted-foreground" style={{
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            fontWeight: 500,
            letterSpacing: 0,
            lineHeight: 1.4
          }}>
              {"// Consultation available — book your slot"}
            </p>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-5 w-full" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 420ms cubic-bezier(0.2, 0.9, 0.3, 1) 120ms, transform 420ms cubic-bezier(0.2, 0.9, 0.3, 1) 120ms"
        }}>
            <div className="relative flex flex-col gap-0">
              {pipeline.map((node, i) => {
              const Icon = node.icon;
              const isLive = node.state === "live";
              return <div data-index={i} key={node.label} className="flex flex-col items-stretch">
                    <div className="border-2 border-foreground rounded-lg p-5 flex items-center justify-between gap-4" style={{
                  background: isLive ? "var(--secondary)" : "var(--card)",
                  color: isLive ? "var(--secondary-foreground)" : "var(--foreground)"
                }}>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center size-10 border-2 rounded-lg shrink-0" style={{
                      borderColor: isLive ? "var(--secondary-foreground)" : "var(--foreground)"
                    }}>
                          <Icon className="size-5" strokeWidth={2} />
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold uppercase" style={{
                        fontSize: "0.75rem",
                        letterSpacing: "0.04em",
                        lineHeight: 1.3
                      }}>
                            {"Phase 0" + (i + 1)}
                          </span>
                          <span className="font-default font-bold" style={{
                        fontFamily: "var(--font-default)",
                        fontSize: "1.25rem",
                        letterSpacing: "-0.02em",
                        lineHeight: 1.05
                      }}>
                            {node.label}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {isLive && <span className="inline-block size-2.5 rounded-lg" style={{
                      background: "var(--secondary-foreground)",
                      animation: "pulse 1.8s ease-in-out infinite"
                    }} aria-hidden="true" />}
                        <span className="font-mono font-medium" style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.8125rem",
                      letterSpacing: 0
                    }}>
                          {node.status}
                        </span>
                      </div>
                    </div>

                    {}
                    {i < pipeline.length - 1 && <div className="flex justify-start pl-9 py-1" aria-hidden="true">
                        <div className="w-0.5 h-6 bg-border" />
                      </div>}
                  </div>;
            })}

              {}
              <div className="mt-6 flex items-center justify-between border-t-2 border-border pt-4">
                <span className="inline-flex items-center gap-2 font-semibold uppercase text-muted-foreground" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.04em"
              }}>
                  <Activity className="size-3.5" strokeWidth={2.5} />
                  Project status
                </span>
                <span className="font-mono text-muted-foreground" style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.8125rem"
              }}>
                  {"active — ready to start"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </section>;
}